import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';

import { useRequired } from 'helpers/hooks';
import { FieldsWrapper } from 'components/FieldsWrapper';
import { Input } from 'components/Input';
import { useFilter } from "helpers/hooks";
import { DatePicker } from "./DatePicker";
import { useLocation } from 'react-router-dom';
import { AutocompleteSelect } from 'components/AutocompleteSelect';

export const Payment = ({ user, paymentTypes, accounts, editable, isAccountant, fetchRequiredFields, excludedKeys, isCashDeskDocType }) => {
    const { values, setFieldValue } = useFormikContext();
    let [manualFire, setManualFire] = useState(0);

    // Unique session key per record to persist mode across re-renders
    const storageKey = `payment_account_mode_${values.info?.id || 'default'}`;

    // Read persisted mode from sessionStorage on mount
    const [isSelectAccount, setIsSelectAccountState] = useState(() => {
        return sessionStorage.getItem(storageKey) === 'db';
    });

    const [previousDependencies, setPreviousDependencies] = useState({
        payment_type: values.info?.payment_type,
        account: values.info?.account,
        supplier_account_number: values.info?.supplier_account_number,
        supplier_bank_code: values.info?.supplier_bank_code,
        variable_symbol: values.info?.variable_symbol,
        payment_date: values.info?.payment_date,
    });

    let location = useLocation();
    const modify = location.pathname.split("/")[2] === "modify";
    
    let requiredFields = fetchRequiredFields(user, values?.info?.["document_type"], values?.info?.["payment_type"]);
    
    const filteredAccounts = useFilter(accounts);
    const accountNumberRequired = useRequired('supplier_account_number', requiredFields);
    const bankCodeRequired = useRequired('supplier_bank_code', requiredFields);
    const variableSymbolRequired = useRequired('variable_symbol', requiredFields);
    
    let accountOnly = ['fvy', 'fvz', 'opo'].includes(values.info?.document_type);
    let isCash = paymentTypes.find(type => type.id === parseInt(values.info?.payment_type))?.code === 'cash';
    const accountEditable = editable && values.info?.client_company;
    const [refresh, setRefresh] = useState(true);

    if (values.info?.["state"] === 'tpl') {
        excludedKeys = [];
    }
    let modifyToggles = JSON.parse(sessionStorage.getItem("bulkDetailModifyToggles"));
    if (modify === true && modifyToggles?.any_account_only && !values.info?.document_type) {
        accountOnly = true;
    }
    if (modify === true && modifyToggles?.any_cash && !values.info?.payment_type) {
        isCash = true;
    }

    const handleToggleAccountMode = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const nextMode = !isSelectAccount;
        
        sessionStorage.setItem(storageKey, nextMode ? 'db' : 'manual');
        setIsSelectAccountState(nextMode);

        if (nextMode) {
            if (setFieldValue) {
                setFieldValue('info.supplier_account_number', '');
                setFieldValue('info.supplier_bank_code', '');
            } else {
                values.info.supplier_account_number = '';
                values.info.supplier_bank_code = '';
            }
        } else {
            if (setFieldValue) {
                setFieldValue('info.account', '');
            } else {
                values.info.account = '';
            }
        }
        setRefresh((prev) => !prev);
    };

    useEffect(() => {
        const dependencies = {
            payment_type: values.info?.payment_type,
            account: values.info?.account,
            supplier_account_number: values.info?.supplier_account_number,
            supplier_bank_code: values.info?.supplier_bank_code,
            variable_symbol: values.info?.variable_symbol,
            payment_date: values.info?.payment_date,
        };
    
        for (const key in dependencies) {
            if (dependencies[key] !== previousDependencies[key]) {
                if (values.changedValues && !values.changedValues.includes(key)) {
                    values.changedValues.push(key);
                }
            }
        }
        if (JSON.stringify(dependencies) !== JSON.stringify(previousDependencies)) {
            setPreviousDependencies(dependencies);
            setRefresh(!refresh); 
        }
    }, [values.info, manualFire, previousDependencies, refresh, values.changedValues]);

    return (
        <FieldsWrapper columns={2}>
            {!isCashDeskDocType && (
                <Input
                    name="info.payment_type"
                    component={AutocompleteSelect}
                    data={paymentTypes}
                    disabled={!editable}
                    highlighted={modify && values.changedValues.includes("payment_type")}
                    selectedValue={values.info?.["payment_type"]} 
                    onChange={(newValue) => {
                        if (setFieldValue) {
                            setFieldValue("info.payment_type", newValue);
                        } else {
                            values.info["payment_type"] = newValue;
                        }
                        setRefresh(!refresh);
                        setManualFire(manualFire + 1);
                    }} 
                >
                    Forma úhrady
                </Input>
            )}

            {accountOnly && !isCashDeskDocType ? (
                <div style={{ position: 'relative', paddingTop: '4px' }}>
                    {/* Positioned button with increased text size */}
                    <div style={{ position: 'absolute', top: '-2px', right: 0, zIndex: 2 }}>
                        <button
                            type="button"
                            onClick={handleToggleAccountMode}
                            style={{
                                cursor: 'pointer',
                                fontSize: '13px',
                                backgroundColor: 'var(--light)',
                                border: '1px solid var(--primary)',
                                borderRadius: '12px',
                                padding: '2px 10px',
                                userSelect: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                lineHeight: '1.2',
                                outline: 'none'
                            }}
                            title="Přepnout způsob zadání účtu"
                        >
                            <span style={{ 
                                fontWeight: !isSelectAccount ? '700' : '400', 
                                color: !isSelectAccount ? 'var(--primary)' : '#64748b' 
                            }}>
                                Zadat ručně
                            </span>
                            <span style={{ color: '#94a3b8', fontWeight: 'normal' }}>/</span>
                            <span style={{ 
                                fontWeight: isSelectAccount ? '700' : '400', 
                                color: isSelectAccount ? 'var(--primary)' : '#64748b' 
                            }}>
                                Databáze
                            </span>
                        </button>
                    </div>

                    {isSelectAccount ? (
                        <Input
                            name="info.account"
                            component={AutocompleteSelect}
                            data={filteredAccounts}
                            selectedValue={values.info?.["account"]} 
                            highlighted={modify && values.changedValues.includes("account")}
                            disabled={!accountEditable}
                            isHidden={isCash}
                            onChange={(newValue) => {
                                if (setFieldValue) {
                                    setFieldValue("info.account", newValue);
                                } else {
                                    values.info["account"] = newValue;
                                }
                                setRefresh(!refresh);
                                setManualFire(manualFire + 1);
                            }} 
                        >
                            Účet
                        </Input>
                    ) : (
                        <>
                            <Input
                                name="info.supplier_account_number"
                                disabled={!editable}
                                isRequired={accountNumberRequired}
                                isHidden={isCash}
                                highlighted={modify && values.changedValues.includes("supplier_account_number")}
                            >
                                Číslo účtu / IBAN
                            </Input>
                            <Input
                                name="info.supplier_bank_code"
                                disabled={!editable}
                                isRequired={bankCodeRequired}
                                isHidden={isCash}
                                highlighted={modify && values.changedValues.includes("supplier_bank_code")}
                            >
                                Kód banky / SWIFT
                            </Input>
                        </>
                    )}
                </div>
            ) : (
                <>
                    {!isCashDeskDocType && (
                        <Input
                            name="info.supplier_account_number"
                            disabled={!editable}
                            isRequired={accountNumberRequired}
                            isHidden={isCash}
                            highlighted={modify && values.changedValues.includes("supplier_account_number")}
                        >
                            Číslo účtu / IBAN
                        </Input>
                    )}
                    {!isCashDeskDocType && (
                        <Input
                            name="info.supplier_bank_code"
                            disabled={!editable}
                            isRequired={bankCodeRequired}
                            isHidden={isCash}
                            highlighted={modify && values.changedValues.includes("supplier_bank_code")}
                        >
                            Kód banky / SWIFT
                        </Input>
                    )}
                </>
            )}

            {!isCashDeskDocType && (
                <Input
                    name="info.variable_symbol"
                    disabled={!editable}
                    isRequired={variableSymbolRequired}
                    isHidden={isCash}
                    highlighted={modify && values.changedValues.includes("variable_symbol")}
                >
                    Variabilní symbol
                </Input>
            )}

            {!excludedKeys.includes("payment_date") ? (
                <div style={{ height: '0em' }}>&nbsp;</div>
            ) : (
                <Input
                    name="info.payment_date"
                    component={DatePicker}
                    disabled={!editable}
                    highlighted={modify && values.changedValues.includes("payment_date")}
                >
                    Datum úhrady
                </Input>
            )}
        </FieldsWrapper>
    );
};